import React, { useCallback, useEffect, useRef, useState } from 'react';
import { clamp } from '../lodash';
import { cnEditorComponent } from './YfmEditorView';
export const IN_RESIZE_CLASSNAME = 'in-resize';
const useColResize = ({ onStart, onMove, onEnd }) => {
    const initialXPosition = useRef(0);
    const currentXPosition = useRef(0);
    const handleMove = useCallback((e) => {
        var _a, _b;
        const currentX = 'touches' in e ? (_b = (_a = e.touches[0]) === null || _a === void 0 ? void 0 : _a.clientX) !== null && _b !== void 0 ? _b : 0 : e.clientX;
        if (currentXPosition.current === currentX) {
            return;
        }
        currentXPosition.current = currentX;
        const delta = initialXPosition.current - currentX;
        onMove(delta);
    }, [onMove]);
    const handleDragEnd = useCallback((e) => {
        var _a, _b;
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('touchmove', handleMove);
        document.body.style.removeProperty('user-select');
        const currentX = 'touches' in e ? (_b = (_a = e.touches[0]) === null || _a === void 0 ? void 0 : _a.clientX) !== null && _b !== void 0 ? _b : 0 : e.clientX;
        const delta = initialXPosition.current - currentX;
        onEnd(delta);
    }, [handleMove, onEnd]);
    const handleStart = useCallback((e) => {
        var _a, _b;
        const currentX = 'touches' in e ? (_b = (_a = e.touches[0]) === null || _a === void 0 ? void 0 : _a.clientX) !== null && _b !== void 0 ? _b : 0 : e.clientX;
        initialXPosition.current = currentX;
        currentXPosition.current = currentX;
        window.addEventListener('mouseup', handleDragEnd, { once: true });
        window.addEventListener('touchend', handleDragEnd, { once: true });
        window.addEventListener('touchcancel', handleDragEnd, { once: true });
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('touchmove', handleMove);
        document.body.style.setProperty('user-select', 'none');
        onStart();
    }, [handleDragEnd, handleMove, onStart]);
    return {
        listeners: {
            onMouseDown: handleStart,
            onTouchStart: handleStart,
        },
    };
};
const HorizontalDrag = ({ leftElRef, rightElRef, wrapperRef, editor, }) => {
    var _a, _b, _c, _d;
    const [lCardWidth, lSetCardWidth] = useState(((_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) !== null && _b !== void 0 ? _b : 0) / 2);
    const [rCardWidth, rSetCardWidth] = useState(((_d = (_c = wrapperRef.current) === null || _c === void 0 ? void 0 : _c.clientWidth) !== null && _d !== void 0 ? _d : 0) / 2);
    const updateWidth = useCallback((lNewWidth, rNewWidth) => {
        var _a, _b, _c, _d;
        (_b = (_a = leftElRef.current) === null || _a === void 0 ? void 0 : _a.style) === null || _b === void 0 ? void 0 : _b.setProperty('width', `${lNewWidth}px`);
        (_d = (_c = rightElRef.current) === null || _c === void 0 ? void 0 : _c.style) === null || _d === void 0 ? void 0 : _d.setProperty('width', `${rNewWidth}px`);
    }, [leftElRef, rightElRef]);
    const calculateWidth = useCallback((delta) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return [
            clamp(lCardWidth - delta, ((_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) !== null && _b !== void 0 ? _b : 0) / 8, (((_d = (_c = wrapperRef.current) === null || _c === void 0 ? void 0 : _c.clientWidth) !== null && _d !== void 0 ? _d : 0) * 3) / 4),
            clamp(rCardWidth + delta, ((_f = (_e = wrapperRef.current) === null || _e === void 0 ? void 0 : _e.clientWidth) !== null && _f !== void 0 ? _f : 0) / 8, (((_h = (_g = wrapperRef.current) === null || _g === void 0 ? void 0 : _g.clientWidth) !== null && _h !== void 0 ? _h : 0) * 3) / 4),
        ];
    }, [lCardWidth, rCardWidth, wrapperRef]);
    // Set initially calculated width
    useEffect(() => {
        updateWidth(lCardWidth, rCardWidth);
        editor.cm.refresh();
    }, []);
    useEffect(() => {
        const [leftElCurrent, rightElCurrent] = [leftElRef.current, rightElRef.current];
        return () => {
            var _a, _b;
            leftElCurrent === null || leftElCurrent === void 0 ? void 0 : leftElCurrent.classList.remove(IN_RESIZE_CLASSNAME);
            rightElCurrent === null || rightElCurrent === void 0 ? void 0 : rightElCurrent.classList.remove(IN_RESIZE_CLASSNAME);
            (_a = leftElCurrent === null || leftElCurrent === void 0 ? void 0 : leftElCurrent.style) === null || _a === void 0 ? void 0 : _a.removeProperty('width');
            (_b = rightElCurrent === null || rightElCurrent === void 0 ? void 0 : rightElCurrent.style) === null || _b === void 0 ? void 0 : _b.removeProperty('width');
        };
    }, [leftElRef, rightElRef]);
    const onStart = useCallback(() => {
        var _a, _b, _c;
        (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.style.setProperty('user-select', 'none');
        (_b = leftElRef.current) === null || _b === void 0 ? void 0 : _b.classList.add(IN_RESIZE_CLASSNAME);
        (_c = rightElRef.current) === null || _c === void 0 ? void 0 : _c.classList.add(IN_RESIZE_CLASSNAME);
    }, [leftElRef, rightElRef, wrapperRef]);
    const onMove = useCallback((delta) => {
        const [lNewWidth, rNewWidth] = calculateWidth(delta);
        updateWidth(lNewWidth, rNewWidth);
    }, [calculateWidth, lCardWidth, rCardWidth, updateWidth]);
    const onEnd = useCallback((delta) => {
        var _a, _b, _c;
        const [lNewWidth, rNewWidth] = calculateWidth(delta);
        lSetCardWidth(lNewWidth);
        rSetCardWidth(rNewWidth);
        updateWidth(lNewWidth, rNewWidth);
        (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.style.removeProperty('user-select');
        editor.cm.refresh();
        (_b = rightElRef.current) === null || _b === void 0 ? void 0 : _b.classList.remove(IN_RESIZE_CLASSNAME);
        (_c = leftElRef.current) === null || _c === void 0 ? void 0 : _c.classList.remove(IN_RESIZE_CLASSNAME);
    }, [calculateWidth, editor.cm, leftElRef, rightElRef, updateWidth, wrapperRef]);
    const { listeners } = useColResize({ onStart, onMove, onEnd });
    return (React.createElement("div", Object.assign({ className: cnEditorComponent('resizer', { horizontal: true }) }, listeners),
        React.createElement("div", { className: cnEditorComponent('gutter') })));
};
const HorizontalDragWrapper = (props) => {
    // If component calling HorizontalDrag is not mounted, then most likely wrapperRef will be null
    if (!props.isMounted)
        return (React.createElement("div", { className: cnEditorComponent('resizer', { horizontal: true }) },
            React.createElement("div", { className: cnEditorComponent('gutter') })));
    return React.createElement(HorizontalDrag, Object.assign({}, props));
};
export { HorizontalDragWrapper as HorizontalDrag };
